import { matchCall } from "../domUtils";
import type { PrototypeState } from "../controllerState";
import type { Root } from "../prototypeTypes";
import { backFromContextualView, backToCommunity, openModal, setActiveTab, setSocialSubTab, setThemeMode, showContextualView, showSocial, showView } from "../navigationController";
import { backFromTopic, backToActiveTopic, showTopicDetail } from "../topicDetailView";
import { recordRuntimeError } from "../prototypeRuntimeSupport";
import { showIdeaDetail } from "../ideaDetailController";
import { closeCommunityIdeaDialog, handoffCommunityIdeaDialogTopic, openCommunityIdeaDialog, retryCommunityIdeaDialog } from '../communityIdeaDialogController';
import { retryIdeaDetail } from '../ideaDetailController';
import { backFromAiJobDetail, showAiJobDetail } from "../aiJobDetailController";
import { closeAiSubmissionDialog, handoffAiSubmissionDialogTopic, openAiSubmissionDialog, retryAiSubmissionDialog } from '../aiSubmissionDialogController';
import { loadViewData } from "./contextLoaders";
import { captureCommunityReturnState } from '../communityMountState';

export function routeNavigationAction(normalized: string, root: Root, state: PrototypeState, source?: HTMLElement) {
  const view = matchCall(normalized, "showView");
  if (view) {
    showView(view, root);
    void loadViewData(view, state.runtime, state, root);
  }
  if (normalized.includes("backToActiveTopic")) {
    const topicId = state.runtime.activeCourseId;
    if (topicId) backToActiveTopic(topicId, root, state.effectiveAccessPlan, state.runtime);
    else showView("topic", root);
  }
  if (normalized.includes("backFromTopic")) {
    backFromTopic(root);
  }
  const activeTab = matchCall(normalized, "setActiveTab");
  if (activeTab) setActiveTab(activeTab, root);
  const theme = matchCall(normalized, "setThemeMode");
  if (theme) setThemeMode(theme, root);
  // Social view: showSocial('community') or showSocial('github')
  const socialSub = matchCall(normalized, "showSocial");
  if (socialSub) {
    showSocial(socialSub as "community" | "github", root);
  }
  // Smart back from idea-detail: PC -> #view-community, mobile -> #view-social
  if (normalized.includes("backToCommunity")) {
    backToCommunity(root);
  }
  // Social sub-tab switch: setSocialSubTab('community') or setSocialSubTab('github')
  const setSocialSub = matchCall(normalized, "setSocialSubTab");
  if (setSocialSub) {
    setSocialSubTab(setSocialSub as "community" | "github", root);
  }
	const ideaId = matchCall(normalized, "showIdeaDetail");
  if (ideaId) {
    try {
		const communityReturnState = source ? captureCommunityReturnState(root, source) : null;
		const currentState = window.history.state && typeof window.history.state === 'object' ? window.history.state : {};
		const { communityReturnState: staleCommunityReturnState, ...stateWithoutCommunityReturn } = currentState;
		if (communityReturnState || staleCommunityReturnState !== undefined) {
			window.history.replaceState(communityReturnState
				? { ...stateWithoutCommunityReturn, communityReturnState }
				: stateWithoutCommunityReturn, '');
		}
      showIdeaDetail(ideaId, root);
    } catch (error) {
      recordRuntimeError(state.runtime, error);
    }
  }
	const dialogIdeaId = matchCall(normalized, 'openCommunityIdeaDialog');
	if (dialogIdeaId) openCommunityIdeaDialog(root, dialogIdeaId, source);
	const dialogRetryId = matchCall(normalized, 'retryCommunityIdeaDialog');
	if (dialogRetryId) retryCommunityIdeaDialog(root, dialogRetryId);
	if (normalized.includes('closeCommunityIdeaDialog')) closeCommunityIdeaDialog(root);
	const communityTopicSlug = matchCall(normalized, 'showCommunityIdeaTopic');
	if (communityTopicSlug) {
		handoffCommunityIdeaDialogTopic(root, () => {
			state.runtime.activeCourseId = communityTopicSlug;
			state.runtime.activeChapterIndex = 1;
			showTopicDetail(communityTopicSlug, root, state.effectiveAccessPlan);
			setActiveTab('', root);
		});
	}
	const pageRetryId = matchCall(normalized, 'retryIdeaDetail');
	if (pageRetryId) retryIdeaDetail(pageRetryId, root);
  const aiJobId = matchCall(normalized, "showAiJobDetail");
  if (aiJobId) {
    try {
      showAiJobDetail(aiJobId, root);
    } catch (error) {
      recordRuntimeError(state.runtime, error);
    }
  }
  if (normalized.includes('backFromAiJobDetail')) backFromAiJobDetail(root);
  // AI Create submission reader dialog (completed topic cards).
  const aiSubmissionJobId = matchCall(normalized, 'openAiSubmissionDialog');
  if (aiSubmissionJobId) openAiSubmissionDialog(root, aiSubmissionJobId, source);
  const aiSubmissionRetryId = matchCall(normalized, 'retryAiSubmissionDialog');
  if (aiSubmissionRetryId) retryAiSubmissionDialog(root, aiSubmissionRetryId);
  if (normalized.includes('closeAiSubmissionDialog')) closeAiSubmissionDialog(root);
  const aiSubmissionTopicSlug = matchCall(normalized, 'showAiSubmissionTopic');
  if (aiSubmissionTopicSlug) {
    handoffAiSubmissionDialogTopic(root, () => {
      state.runtime.activeCourseId = aiSubmissionTopicSlug;
      state.runtime.activeChapterIndex = 1;
      showTopicDetail(aiSubmissionTopicSlug, root, state.effectiveAccessPlan);
      setActiveTab('', root);
    });
  }
	if (normalized.includes('showMembershipBilling')) {
		showContextualView('membership-billing', root);
		setActiveTab('profile', root);
	}
	if (normalized.includes('backFromMembershipBilling')) backFromContextualView(root, { view: 'profile', tab: 'profile' });
	if (normalized.includes('showRedeem')) {
		showContextualView('redeem', root);
		setActiveTab('profile', root);
	}
	if (normalized.includes('backFromRedeem')) backFromContextualView(root, { view: 'profile', tab: 'profile' });
}
